// Pure-function port of the math used by MarkdownReportRenderer.cs.
// CTR math, per-market aggregation, category classification.
//
// Keep this layer free of React — it's called from the section
// components but does no I/O.

import { NON_CTR_TYPES, CAMPAIGN_TYPE_ORDER, FALLBACK_ZIPS, SEGMENT_PATTERN, ZIP_SCOPED_MARKET } from './report-config';
import { classifyCategory } from './category-classifier';
import { resolveTitle } from './title-resolver';

// --- CTR helpers -------------------------------------------------------------
// Unweighted mean of per-row clicks/impressions × 100. Matches the CLI's
// DailyMeanCtr (which matches the dashboard's Looker AVG(Ctr)).

export function dailyMeanCtr(rows) {
  let sum = 0;
  let n = 0;
  for (const r of rows || []) {
    if (!r.impressions || r.impressions <= 0) continue;
    sum += r.clicks / r.impressions * 100;
    n++;
  }
  return n === 0 ? 0 : sum / n;
}
export function clusterCtrExcludingVideo(rows) {
  return dailyMeanCtr((rows || []).filter(r => !NON_CTR_TYPES.has(r.type)));
}

// --- Campaign bucketing ------------------------------------------------------
// Group rows by Type → { campaigns: Set<string>, impressions, videoViews,
// clicks, cost }, ordered per CAMPAIGN_TYPE_ORDER.

export function bucketCampaignsByType(rows) {
  const buckets = new Map();
  for (const r of rows || []) {
    if (!buckets.has(r.type)) {
      buckets.set(r.type, {
        type: r.type,
        campaigns: new Set(),
        impressions: 0,
        videoViews: 0,
        clicks: 0,
        cost: 0
      });
    }
    const b = buckets.get(r.type);
    b.campaigns.add(r.campaign);
    b.impressions += r.impressions || 0;
    b.videoViews += r.videoViews || 0;
    b.clicks += r.clicks || 0;
    b.cost += Number(r.cost) || 0;
  }
  // Apply CTR per type bucket (excluded for non-CTR types).
  const ctrByType = new Map();
  const grouped = new Map();
  for (const r of rows || []) {
    if (!grouped.has(r.type)) grouped.set(r.type, []);
    grouped.get(r.type).push(r);
  }
  for (const [type, group] of grouped) {
    ctrByType.set(type, dailyMeanCtr(group));
  }
  const ordered = Array.from(buckets.values()).sort((a, b) => {
    const ai = CAMPAIGN_TYPE_ORDER.indexOf(a.type);
    const bi = CAMPAIGN_TYPE_ORDER.indexOf(b.type);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });
  return ordered.map(b => ({
    type: b.type,
    campaignCount: b.campaigns.size,
    impressions: b.impressions,
    videoViews: b.videoViews,
    clicks: b.clicks,
    cost: b.cost,
    ctr: NON_CTR_TYPES.has(b.type) ? null : ctrByType.get(b.type) || 0
  }));
}
export function totalsForRows(rows) {
  const impressions = (rows || []).reduce((s, r) => s + (r.impressions || 0), 0);
  const clicks = (rows || []).reduce((s, r) => s + (r.clicks || 0), 0);
  const videoViews = (rows || []).reduce((s, r) => s + (r.videoViews || 0), 0);
  const cost = (rows || []).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const campaigns = new Set((rows || []).map(r => r.campaign));
  return {
    impressions,
    clicks,
    videoViews,
    cost,
    campaignCount: campaigns.size,
    ctr: clusterCtrExcludingVideo(rows)
  };
}

// --- Segment campaigns -------------------------------------------------------
// Filter to Social rows whose campaign name matches the C1/C2/C3 pattern;
// group by campaign name; compute per-campaign CTR.

export function segmentBreakdown(rows) {
  const seg = (rows || []).filter(r => r.type === 'Social' && SEGMENT_PATTERN.test(r.campaign));
  const byCamp = new Map();
  for (const r of seg) {
    if (!byCamp.has(r.campaign)) byCamp.set(r.campaign, []);
    byCamp.get(r.campaign).push(r);
  }
  const items = [];
  for (const [campaign, rs] of byCamp) {
    const imp = rs.reduce((s, x) => s + (x.impressions || 0), 0);
    const clk = rs.reduce((s, x) => s + (x.clicks || 0), 0);
    const cost = rs.reduce((s, x) => s + (Number(x.cost) || 0), 0);
    items.push({
      campaign,
      impressions: imp,
      clicks: clk,
      cost,
      ctr: dailyMeanCtr(rs)
    });
  }
  // Sort by family then tier (C1/C2/C3)
  items.sort((a, b) => {
    const ma = a.campaign.match(SEGMENT_PATTERN);
    const mb = b.campaign.match(SEGMENT_PATTERN);
    const famA = ma ? a.campaign.slice(0, ma.index) : a.campaign;
    const famB = mb ? b.campaign.slice(0, mb.index) : b.campaign;
    if (famA !== famB) return famA.localeCompare(famB);
    const ta = ma ? parseInt(ma[0].charAt(2), 10) : 9;
    const tb = mb ? parseInt(mb[0].charAt(2), 10) : 9;
    return ta - tb;
  });
  const totalImp = items.reduce((s, x) => s + x.impressions, 0);
  const totalClk = items.reduce((s, x) => s + x.clicks, 0);
  const totalCost = items.reduce((s, x) => s + x.cost, 0);
  return {
    items,
    total: {
      impressions: totalImp,
      clicks: totalClk,
      cost: totalCost,
      ctr: dailyMeanCtr(seg),
      campaignCount: items.length
    }
  };
}

// --- Per-market product + category aggregation -------------------------------
// Ported from MarkdownReportRenderer.cs:96-158.
//
// Inputs:
//   market: string (the place name)
//   placeZips: ZipStats[] (live zips from /location/stats with $place=)
//   allProductRows: ProductLocationRow[] (the full cluster pull from
//     /product/location/stats with $groupBy=postal_code)
//
// Filters the global product rows down to this market's zip set, then
// groups by productId → top 10 by views, and by category → top 10 by views.

export function aggregateProductsAndCategoriesForMarket(market, placeZips, allProductRows) {
  if (!allProductRows || allProductRows.length === 0) {
    return {
      topProducts: [],
      topCategories: []
    };
  }

  // Build the zip set: prefer live place_zips, fall back to FALLBACK_ZIPS.
  const zipSet = new Set();
  for (const z of placeZips || []) {
    if (z.postalCode) zipSet.add(String(z.postalCode));
  }
  if (zipSet.size === 0 && FALLBACK_ZIPS[market]) {
    for (const z of FALLBACK_ZIPS[market]) zipSet.add(z);
  }
  if (zipSet.size === 0) return {
    topProducts: [],
    topCategories: []
  };
  const marketRows = allProductRows.filter(r => r.postalCode && zipSet.has(String(r.postalCode)));
  if (marketRows.length === 0) return {
    topProducts: [],
    topCategories: []
  };

  // Top products — group by productId, sum across the market's zips.
  // Title is derived from the link slug (raw title has known cross-product leak).
  const byProduct = new Map();
  for (const r of marketRows) {
    const k = r.productId;
    if (!byProduct.has(k)) byProduct.set(k, []);
    byProduct.get(k).push(r);
  }
  const topProducts = [];
  for (const [productId, rs] of byProduct) {
    const first = rs[0];
    const link = rs.map(x => x.link).find(l => l && String(l).trim()) || null;
    const resolvedTitle = resolveTitle(link, first.title);
    topProducts.push({
      productId,
      title: resolvedTitle,
      price: rs.reduce((mx, x) => Math.max(mx, Number(x.price) || 0), 0),
      views: rs.reduce((s, x) => s + (x.views || 0), 0),
      sessions: rs.reduce((s, x) => s + (x.sessions || 0), 0),
      uniqueUsers: rs.reduce((s, x) => s + (x.uniqueUsers || 0), 0),
      conversions: rs.reduce((s, x) => s + (x.conversions || 0), 0),
      conversionsPriceValue: rs.reduce((s, x) => s + (x.conversionsPriceValue || 0), 0),
      engagements: rs.reduce((s, x) => s + (x.engagements || 0), 0),
      category: classifyCategory({
        ...first,
        title: resolvedTitle
      })
    });
  }
  topProducts.sort((a, b) => b.views - a.views);
  const top10Products = topProducts.slice(0, 10);

  // Top categories — group ALL market rows by inferred category. Use
  // link-derived title for classification.
  const byCategory = new Map();
  for (const r of marketRows) {
    const resolved = resolveTitle(r.link, r.title);
    const cat = classifyCategory({
      ...r,
      title: resolved
    });
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat).push(r);
  }
  const topCategories = [];
  for (const [category, rs] of byCategory) {
    const products = new Set(rs.map(x => x.productId)).size;
    topCategories.push({
      category,
      products,
      views: rs.reduce((s, x) => s + (x.views || 0), 0),
      sessions: rs.reduce((s, x) => s + (x.sessions || 0), 0),
      uniqueUsers: rs.reduce((s, x) => s + (x.uniqueUsers || 0), 0),
      conversions: rs.reduce((s, x) => s + (x.conversions || 0), 0),
      conversionsPriceValue: rs.reduce((s, x) => s + (x.conversionsPriceValue || 0), 0)
    });
  }
  topCategories.sort((a, b) => b.views - a.views);
  const top10Categories = topCategories.slice(0, 10);
  return {
    topProducts: top10Products,
    topCategories: top10Categories
  };
}

// --- Cluster totals (excludes zip-scoped market to avoid double-count) -------

export function clusterRows(allCampaignsByMarket) {
  const out = [];
  for (const [market, rows] of Object.entries(allCampaignsByMarket || {})) {
    if (market === ZIP_SCOPED_MARKET) continue; // Wellen Park is a slice of Sarasota
    for (const r of rows || []) out.push(r);
  }
  return out;
}

// --- Synthesize place stats from zip rollups ---------------------------------
// Used when a market isn't in the place_stats index — sum the zip rows
// and weight avg session by sessions.

export function synthesizePlaceStats(name, zips) {
  const sumSessions = (zips || []).reduce((s, z) => s + (z.sessions || 0), 0);
  const avgTs = sumSessions > 0 ? (zips || []).reduce((s, z) => s + (z.avgTimeSpan || 0) * (z.sessions || 0), 0) / sumSessions : 0;
  return {
    name,
    views: (zips || []).reduce((s, z) => s + (z.views || 0), 0),
    sessions: sumSessions,
    uniqueUsers: (zips || []).reduce((s, z) => s + (z.uniqueUsers || 0), 0),
    conversions: (zips || []).reduce((s, z) => s + (z.conversions || 0), 0),
    avgTimeSpan: avgTs
  };
}